import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import DownArrow from "../../components/Icons/DownArrow";
import UpArrow from "../../components/Icons/UpArrow";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { formSchema } from "./types";
import TomSelect from "tom-select";
import "./UpdaterForm.css";
import { setColumnBasedOnYearAndQuarter, setSheetsSelectOptions, setLineItemColumn, setActiveSheet, getContext, } from "../../services/excel";
import { copyToClipboard } from "../../utils/clipboard";
import { determineFiscalPeriodsFromCurrentDate } from "../../utils/fiscal";
import Fuse from "fuse.js";
import { useAuth } from "@clerk/clerk-react";
import { captureErrorWithContext } from "../../utils/sentry";
import { fetchJson, getOptions, HttpMethod } from "../../api/api";
import { TextInput } from "../../components/Form/TextInput";
import { FileUpload } from "../../components/Form/FileUpload";
import Spinner from "../../components/Icons/Spinner";
import { ICON_SIZES } from "../../components/Icons/IconSizes";
import { useFileDragAndDropUploader } from "../../components/Form/FileDragAndDropUploader";
const UpdaterForm = ({ onSubmitStep, onRunUpdate, hasPreviousSteps, defaultValues, defaultMultiSelectOptions, tickers, llmModels, }) => {
    const fuse = useMemo(() => {
        const tickerKeys = ["ticker", "name"];
        return new Fuse(tickers, {
            keys: tickerKeys,
            threshold: 0.3, // controls fuzziness
            includeMatches: true, // for highlighting
        });
    }, [tickers]);
    const [showAdvancedContent, setShowAdvancedContent] = useState(false);
    const [multiSelect, setMultiSelect] = useState();
    const [modelSheetSelectOptions, setModelSheetSelectOptions] = useState([]);
    const { getToken } = useAuth();
    const advancedContentRef = useRef(null);
    const currentYear = new Date().getFullYear();
    // TODO really there should be different year arrays for target v comparison
    const years = Array.from({ length: currentYear + 2 - 2000 }, // +1 for next year, +1 for inclusive
    (_, i) => currentYear + 1 - i);
    /**
     * We set explicit keys here instead of relying on indexes because
     * the keys are used as values, and in the case of quarters, are treated semantically
     * Meaning 1 -> Q1
     */
    const quarters = {
        0: "Year",
        1: "Q1",
        2: "Q2",
        3: "Q3",
        4: "Q4",
        5: "H1",
        6: "H2",
    };
    const { register, setValue, getValues, handleSubmit, control, formState: { errors, isSubmitted }, } = useForm({
        resolver: zodResolver(formSchema),
        mode: "onTouched", // errors appear once a field is touched
        reValidateMode: "onChange", // after touched, errors re-validate live
        defaultValues: defaultValues ?? {
            overwrite_target: true,
            return_summary: true,
            source_urls: [],
        },
    });
    const sourceUrls = useWatch({ control, name: "source_urls" });
    const prevSourceUrlsRef = useRef(sourceUrls);
    const [autoCompleteResults, setAutoCompleteResults] = useState([]);
    const [showAutoCompleteResults, setShowAutoCompleteResults] = useState(false);
    const [ticker, setTicker] = useState();
    const [fetchingSourceDocuments, setFetchingSourceDocuments] = useState(false);
    const [userMessage, setUserMessage] = useState();
    const [fetchingModelUpdate, setFetchingModelUpdate] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const debounceTimeoutRef = useRef(undefined);
    const [documentIsUploading, setDocumentIsUploading] = useState(false);
    const setFiscalPeriodColumns = async (shouldValidate) => {
        try {
            await getContext(async (context) => {
                await setColumnBasedOnYearAndQuarter(context, setValue, "comparison_column", getValues("comparison_year"), getValues("comparison_quarter"), shouldValidate);
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: {
                    note: "Failed when running setColumnBasedOnYearAndQuarter",
                    payload: JSON.stringify({
                        fieldName: "comparison_column",
                        year: getValues("comparison_year"),
                        quarter: getValues("comparison_quarter"),
                    }),
                },
                tags: {
                    priority: "medium",
                },
            });
        }
        try {
            await getContext(async (context) => {
                await setColumnBasedOnYearAndQuarter(context, setValue, "target_column", getValues("target_year"), getValues("target_quarter"), shouldValidate);
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: {
                    note: "Failed when running setColumnBasedOnYearAndQuarter",
                    payload: JSON.stringify({
                        fieldName: "target_column",
                        year: getValues("target_year"),
                        quarter: getValues("target_quarter"),
                    }),
                },
                tags: {
                    priority: "high",
                },
            });
        }
    };
    const autoSelectFormFields = async () => {
        // If we have default values from a previous step, use those
        if (defaultValues)
            return;
        // Determine likely target and comparison fiscal periods based on current date
        const fiscalPeriods = determineFiscalPeriodsFromCurrentDate();
        // Set target year, quarter, and column from regex scan
        setValue("target_year", fiscalPeriods.target.year);
        if (fiscalPeriods.target.quarter)
            setValue("target_quarter", fiscalPeriods.target.quarter);
        // Set comparison year, quarter, and column from regex scan
        setValue("comparison_year", fiscalPeriods.comparison.year);
        if (fiscalPeriods.comparison.quarter)
            setValue("comparison_quarter", fiscalPeriods.comparison.quarter);
        setFiscalPeriodColumns(false);
        // Set the line items column based on heuristics
        try {
            await getContext(async (context) => {
                setLineItemColumn(context, setValue, "line_items_column", false);
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: {
                    note: "Failed when running setLineItemColumn",
                },
                tags: {
                    priority: "medium",
                },
            });
        }
        // Set the active sheet
        try {
            await getContext(async (context) => {
                setActiveSheet(context, setValue, "model_sheet", false);
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: {
                    note: "Failed when running setActiveSheet",
                },
                tags: {
                    priority: "medium",
                },
            });
        }
    };
    useEffect(() => {
        /**
         * Auto-select the first LLM model when options are available
         * and no model is currently selected or when defaultValues don't include llm_model
         */
        if (llmModels.length > 0 && !getValues("llm_model")) {
            setValue("llm_model", llmModels[0].id, { shouldValidate: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [llmModels]);
    useEffect(() => {
        /**
         * Initialize Tom Select multi-selector
         */
        const select = new TomSelect("#source_urls", {
            valueField: "url",
            labelField: "title",
            searchField: ["title"],
            create: true,
            hidePlaceholder: true,
            onItemAdd: () => {
                select.close();
            },
            onChange: (value) => {
                // TODO in our vanilla version this has failed for unknown reasons. this.options.values being undefined
                // granted it works differently here. may need more breadcrumbs
                // Coerce value to array of strings for RHF / Zod
                const valueAsArray = value ? value.split(",").filter(Boolean) : [];
                setValue("source_urls", valueAsArray, { shouldValidate: true });
            },
            render: {
                item: function (data, escape) {
                    // What appears in the input field after selection
                    return `<div class="selected-item">${escape(data.url)}</div>`;
                },
            },
        });
        // Add copy-to-clipboard feature
        select.on("item_select", (element) => {
            const id = element.dataset.value;
            if (id) {
                const option = select.options[id];
                if (option?.url) {
                    copyToClipboard(element, option.url);
                }
            }
        });
        /**
         * If we are loading the component with cached options from a previous
         * step, then we pre-populate Tom Select.
         */
        if (defaultMultiSelectOptions) {
            Object.values(defaultMultiSelectOptions).forEach((opt, index) => {
                select?.addOption({
                    id: index,
                    title: opt.title,
                    url: opt.url,
                    fiscal_year: opt.fiscal_year,
                    fiscal_quarter: opt.fiscal_quarter,
                    fiscal_half: opt.fiscal_half,
                });
            });
        }
        setMultiSelect(select);
        // Set model sheet dropdown options
        const asyncSetSheetsSelectOptions = async () => {
            try {
                await getContext(async (context) => {
                    setSheetsSelectOptions(context, setModelSheetSelectOptions);
                });
            }
            catch (error) {
                captureErrorWithContext(error, {
                    extra: {
                        note: "Failed when running setSheetsSelectOptions",
                    },
                    tags: {
                        priority: "high",
                    },
                });
            }
        };
        asyncSetSheetsSelectOptions();
        /**
         * Auto-select form fields we are able to
         * If we have default values from a previous step, we will use those instead
         */
        autoSelectFormFields();
        return () => select.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        /**
         * We can't attach this straight to our markup, as the element we want
         * to outline on error is dynamically created by Tom Select
         */
        // This is the `.ts-wrapper` div
        const wrapper = multiSelect?.wrapper;
        if (!wrapper)
            return;
        if (errors.source_urls) {
            wrapper.classList.add("form-validation-fail");
        }
        else {
            wrapper.classList.remove("form-validation-fail");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors.source_urls]);
    useEffect(() => {
        // Skip if value hasn't changed
        if (JSON.stringify(prevSourceUrlsRef.current) === JSON.stringify(sourceUrls)) {
            return;
        }
        prevSourceUrlsRef.current = sourceUrls;
        if (!sourceUrls?.length) {
            return;
        }
        if (!multiSelect) {
            return;
        }
        // Map URLs to full options
        const selectedOptions = mapSourceUrlsToOptions(sourceUrls);
        if (!selectedOptions.length)
            return;
        const sameYear = selectedOptions.every((opt) => opt.fiscal_year === selectedOptions[0].fiscal_year);
        const sameQuarter = selectedOptions.every((opt) => opt.fiscal_quarter === selectedOptions[0].fiscal_quarter);
        /**
         * If all selected URLs are for the same fiscal year and quarter
         * then we can attempt to determine the columns
         */
        if (sameYear && sameQuarter) {
            const year = selectedOptions[0].fiscal_year;
            setValue("target_year", year);
            setValue("comparison_year", year - 1);
            const quarter = selectedOptions[0].fiscal_quarter;
            if (quarter) {
                setValue("target_quarter", quarter);
                setValue("comparison_quarter", quarter);
            }
            setFiscalPeriodColumns(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceUrls]);
    const mapSourceUrlsToOptions = (source_urls) => {
        return source_urls
            .map((url) => Object.keys(multiSelect?.options)
            .map((key) => multiSelect?.options[key])
            .find((opt) => opt?.url === url))
            .filter(Boolean);
    };
    const handleFiscalPeriodChange = (fieldName, fieldValue) => {
        if (fieldName == "target_year") {
            setValue("comparison_year", parseInt(fieldValue) - 1);
        }
        if (fieldName == "comparison_year") {
            setValue("target_year", parseInt(fieldValue) + 1);
        }
        if (fieldName == "target_quarter") {
            setValue("comparison_quarter", parseInt(fieldValue));
        }
        if (fieldName == "comparison_quarter") {
            setValue("target_quarter", parseInt(fieldValue));
        }
        setFiscalPeriodColumns(true);
    };
    const handleWrapperClick = () => {
        if (multiSelect) {
            multiSelect.open();
        }
    };
    const onSubmit = async (data) => {
        // Validate form
        const parsedData = formSchema.parse(data);
        // Clear user message
        setUserMessage(null);
        // Set states indicating form submission
        setFetchingModelUpdate(true);
        setFormSubmitted(true);
        /* eslint-disable react-hooks/purity */
        const startTime = performance.now();
        const userMessage = await onRunUpdate(parsedData, true);
        const endTime = performance.now();
        /* eslint-enable react-hooks/purity */
        userMessage.message += `\nTime taken: ${Math.round((endTime - startTime) / 1000)}s`;
        setUserMessage(userMessage);
        window.scrollTo(0, document.body.scrollHeight);
        setFetchingModelUpdate(false);
    };
    const onAddStep = (data) => {
        // Validate the form
        const parsedData = formSchema.parse(data);
        // Grab our current multi-select options to cache
        const multiSelectOptions = multiSelect?.options;
        onSubmitStep(parsedData, multiSelectOptions);
    };
    const toggleAdvancedState = () => {
        // Set to opposite of current. True->False, False->True
        setShowAdvancedContent((previousState) => !previousState);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async function fetchSourceDocuments(event) {
        setShowAutoCompleteResults(false);
        const newTicker = event.target.value;
        if (newTicker === "" || newTicker === ticker) {
            return false;
        }
        setFetchingSourceDocuments(true);
        setTicker(newTicker);
        multiSelect?.setValue("");
        multiSelect?.clearOptions();
        let options;
        try {
            options = await getOptions({
                getToken: getToken,
                method: HttpMethod.get,
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: { note: "Failed in fetchSourceDocuments()" },
                tags: { priority: "high" },
            });
            return false;
        }
        const response = await fetchJson(`/source-documents/${newTicker}`, options);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in fetchSourceDocuments",
                },
                tags: {
                    priority: "high",
                },
            });
            return false;
        }
        response.data.document_urls.forEach((document_url, index) => {
            const releaseTitle = createReleaseTitle(document_url);
            multiSelect?.addOption({
                id: index,
                title: releaseTitle,
                url: document_url.url,
                fiscal_year: document_url.fiscal_year,
                fiscal_quarter: document_url.fiscal_quarter,
                fiscal_half: document_url.fiscal_half,
            });
        });
        setFetchingSourceDocuments(false);
    }
    function createReleaseTitle(document_url) {
        let fiscal_period = "";
        if (document_url["fiscal_quarter"]) {
            fiscal_period = `Q${document_url["fiscal_quarter"]}`;
        }
        else if (document_url["fiscal_half"]) {
            fiscal_period = `H${document_url["fiscal_half"]}`;
        }
        let release = `${document_url["fiscal_year"]} ${fiscal_period} ${document_url["type"]}`;
        if (document_url["type"] === "10-K") {
            release = `${document_url["fiscal_year"]} FY ${document_url["type"]}`;
        }
        return release;
    }
    const handleTickerChange = (e) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = window.setTimeout(() => {
            const query = e.target.value.trim();
            if (!query || query == "") {
                // Clear and hide our auto complete results
                setAutoCompleteResults([]);
                setShowAutoCompleteResults(false);
                return;
            }
            // Only get the top 5 matches
            const results = fuse ? fuse.search(query).slice(0, 5) : [];
            setAutoCompleteResults(results);
            setShowAutoCompleteResults(true);
        }, 200);
    };
    const handleDocumentUpload = async (file) => {
        setDocumentIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);
        let options;
        try {
            options = await getOptions({
                getToken: getToken,
                method: HttpMethod.post,
                body: formData,
            });
        }
        catch (error) {
            captureErrorWithContext(error, {
                extra: { note: "Failed in handleDocumentUpload()" },
                tags: { priority: "high" },
            });
            setDocumentIsUploading(false);
            return false;
        }
        const response = await fetchJson(`/document-upload`, options);
        setDocumentIsUploading(false);
        if ("error" in response) {
            // API call returned an error
            captureErrorWithContext(new Error(response.error), {
                extra: {
                    note: "Fetch failed in handleDocumentUpload",
                },
                tags: {
                    priority: "high",
                },
            });
            return false;
        }
        multiSelect?.addOption({
            url: response.data.url,
            title: response.data.url,
        });
        multiSelect?.addItem(response.data.url);
    };
    const { isDragging } = useFileDragAndDropUploader(handleDocumentUpload);
    return (_jsxs("div", { className: "relative", children: [isDragging && (_jsx("div", { className: "fixed inset-0 bg-blue-200 bg-opacity-30 flex items-center justify-center z-50 backdrop-blur-sm", children: "Drop your file here to upload" })), _jsxs("form", { id: "main-form", children: [_jsxs("div", { className: "form-control w-full", children: [_jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "Earnings Release" }) }), _jsxs("label", { className: "input input-bordered flex items-center gap-2 relative", children: [_jsx("span", { className: "flex-none", children: "Ticker" }), _jsx(TextInput, { type: "text", className: "grow", placeholder: "AAPL", id: "ticker", "aria-label": "Ticker", ...register("ticker", {
                                            onChange: handleTickerChange,
                                            onBlur: fetchSourceDocuments,
                                        }) }), showAutoCompleteResults && (_jsx("div", { id: "autocomplete-results", children: autoCompleteResults.length > 0 ? (autoCompleteResults.map((r) => (_jsxs("div", { className: "autocomplete-item text-sm", "data-ticker": r.item.ticker, tabIndex: 0, onMouseDown: () => setValue("ticker", r.item.ticker), onKeyDown: (e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setValue("ticker", r.item.ticker);
                                                }
                                            }, children: [_jsx("span", { className: "font-bold text-blue-500", children: r.item.ticker }), " ", _jsx("span", { className: "text-gray-400", children: r.item.name ?? "" })] }, r.refIndex)))) : (_jsx("div", { className: "autocomplete-no-results", children: _jsx("strong", { children: "No results" }) })) }))] })] }), _jsx("div", { className: "form-control w-full mt-3", children: _jsxs("div", { className: `flex relative items-stretch`, tabIndex: 0, onClick: handleWrapperClick, onKeyDown: (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleWrapperClick();
                                }
                            }, children: [_jsx(TextInput, { id: "source_urls", className: "flex-1 input h-auto min-h-[3rem] w-full input-bordered rounded-tr-none rounded-br-none", placeholder: "Source Document URL", "aria-label": "Source Document URLs", ...register("source_urls") }), _jsx("div", { id: "sec-url-dropdown-button-wrapper", className: "relative min-h-12 flex items-stretch", children: _jsxs("button", { id: "sec-url-dropdown-button", className: `w-full min-h-12 h-full ${multiSelect && Object.keys(multiSelect.options)?.length > 0 ? "bg-blue-500 hover:bg-blue-700" : "bg-gray-500"} text-white font-bold px-4 py-2 rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none text-center`, type: "button", disabled: !multiSelect ||
                                            (multiSelect &&
                                                Object.keys(multiSelect.options)?.length === 0), children: [!fetchingSourceDocuments && (_jsx("svg", { id: "sec-url-dropdown-arrow", className: "w-4 h-4 m-auto", "aria-hidden": "true", xmlns: "http://www.w3.org/2000/svg", fill: "none", viewBox: "0 0 10 6", children: _jsx("path", { stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "m1 1 4 4 4-4" }) })), fetchingSourceDocuments && (_jsxs("svg", { id: "sec-url-fetch-spinner", className: "w-4 h-4 m-auto motion-reduce:hidden animate-spin text-white", fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }))] }) })] }) }), _jsxs("div", { className: "form-control w-full mt-1", children: [documentIsUploading && (_jsx(Spinner, { size: ICON_SIZES.small, additionalClasses: ["bg-blue-500"], color: "white" })), !documentIsUploading && (_jsx("div", { className: "text-xs", children: _jsx(FileUpload, { label: "Upload a Document", onPick: handleDocumentUpload }) }))] }), _jsxs("div", { className: "form-control w-full mt-2", children: [_jsxs("div", { className: "label items-center gap-2", children: [_jsx("span", { className: "label-text", children: "Model Sheet" }), _jsx("span", { className: "label-text", children: "Line Items" })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs("select", { className: `select select-bordered w-full max-w-xs ${errors.model_sheet ? "form-validation-fail" : ""}`, title: "year", id: "model_sheet", "aria-label": "Model Sheet", defaultValue: "", ...register("model_sheet"), children: [_jsx("option", { value: "", disabled: true, children: "Select sheet..." }), modelSheetSelectOptions.map((sheet) => (_jsx("option", { value: sheet, children: sheet }, sheet)))] }), _jsx(TextInput, { type: "text", className: `input input-bordered flex-auto w-16 ${errors.line_items_column ? "form-validation-fail" : ""}`, id: "line_items_column", placeholder: "Col", "aria-label": "Line Items Column", ...register("line_items_column") })] })] }), _jsxs("label", { className: "form-control w-full mt-3", children: [_jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "Target Period" }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("select", { className: `select select-bordered w-full max-w-xs ${errors.target_year ? "form-validation-fail" : ""}`, title: "year", id: "target_year", ...register("target_year", {
                                            onChange: (event) => {
                                                handleFiscalPeriodChange(event.target.name, event.target.value);
                                            },
                                        }), children: years.map((year) => (_jsx("option", { value: year, children: year }, year))) }), _jsx("select", { className: `select select-bordered w-full max-w-xs ${errors.target_quarter ? "form-validation-fail" : ""}`, title: "quarter", id: "target_quarter", ...register("target_quarter", {
                                            onChange: (event) => {
                                                handleFiscalPeriodChange(event.target.name, event.target.value);
                                            },
                                        }), children: Object.entries(quarters).map(([quarterNumeric, quarterString]) => (_jsx("option", { value: quarterNumeric, children: quarterString }, quarterNumeric))) }), _jsx(TextInput, { type: "text", className: `input input-bordered flex-auto w-16 ${errors.target_column ? "form-validation-fail" : ""}`, id: "target_column", placeholder: "Col", "aria-label": "Target Column", ...register("target_column") })] })] }), _jsxs("label", { className: "form-control w-full mt-3", children: [_jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "Comparison Period" }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("select", { className: `select select-bordered w-full max-w-xs ${errors.comparison_year ? "form-validation-fail" : ""}`, title: "year", id: "comparison_year", ...register("comparison_year", {
                                            onChange: (event) => {
                                                handleFiscalPeriodChange(event.target.name, event.target.value);
                                            },
                                        }), children: years.map((year) => (_jsx("option", { value: year, children: year }, year))) }), _jsx("select", { className: `select select-bordered w-full max-w-xs ${errors.comparison_quarter ? "form-validation-fail" : ""}`, title: "quarter", id: "comparison_quarter", ...register("comparison_quarter", {
                                            onChange: (event) => {
                                                handleFiscalPeriodChange(event.target.name, event.target.value);
                                            },
                                        }), children: Object.entries(quarters).map(([quarterNumeric, quarterString]) => (_jsx("option", { value: quarterNumeric, children: quarterString }, quarterNumeric))) }), _jsx(TextInput, { type: "text", className: `input input-bordered flex-auto w-16 ${errors.comparison_column ? "form-validation-fail" : ""}`, id: "comparison_column", placeholder: "Col", "aria-label": "Comparison Column", ...register("comparison_column") })] })] }), _jsxs("div", { className: "form-control flex w-full border-b border-slate-200", children: [_jsx("button", { id: "advanced_accordion", onClick: toggleAdvancedState, className: "w-full flex justify-between items-center py-4", type: "button", children: _jsxs("div", { className: "label", children: [_jsx("span", { className: "label-text", children: "Advanced Options" }), _jsx("span", { id: "advanced_arrow", className: "text-slate-800 transition-transform duration-300", children: showAdvancedContent ? _jsx(UpArrow, {}) : _jsx(DownArrow, {}) })] }) }), _jsxs("div", { id: "advanced_content", ref: advancedContentRef, className: "max-h-0 overflow-hidden transition-all duration-300 ease-in-out pb-3", style: {
                                    maxHeight: showAdvancedContent
                                        ? (advancedContentRef.current?.scrollHeight ?? 0)
                                        : 0,
                                }, children: [_jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "LLM" }) }), _jsx("div", { className: "flex items-center gap-2 pb-2", children: _jsx("select", { className: `select select-bordered w-full ${errors.llm_model ? "form-validation-fail" : ""}`, id: "llm_model", "aria-label": "LLM Model", ...register("llm_model"), children: llmModels.map((llmModel) => (_jsx("option", { value: llmModel.id, children: llmModel.label }, llmModel.id))) }) }), _jsx("div", { className: "label", children: _jsx("span", { className: "label-text", children: "Hot Topics" }) }), _jsx(TextInput, { type: "text", id: "hot_topics", className: `input input-bordered w-full ${errors.hot_topics ? "form-validation-fail" : ""}`, placeholder: "Comma-separated list of topics", ...register("hot_topics") }), _jsx("div", { className: "label", children: _jsx("span", { className: "label-text pt-3", children: "Limit Update Range" }) }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(TextInput, { type: "text", className: `input input-bordered flex-auto w-16 ${errors.first_row_number ? "form-validation-fail" : ""}`, id: "first_row_number", placeholder: "First Row #", ...register("first_row_number") }), _jsx(TextInput, { type: "text", className: `input input-bordered flex-auto w-16 ${errors.last_row_number ? "form-validation-fail" : ""}`, id: "last_row_number", placeholder: "Last Row #", ...register("last_row_number") })] }), _jsx("div", { className: "label", children: _jsx("span", { className: "label-text pt-3", children: "Write Summary Tab" }) }), _jsxs("label", { className: "flex items-left py-2 px-3", children: [_jsx("input", { type: "checkbox", className: `sr-only peer items-left ${errors.return_summary ? "form-validation-fail" : ""}`, id: "return_summary", ...register("return_summary") }), _jsx("div", { className: "relative cursor-pointer w-11 h-6 py-3 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500" })] }), _jsx("div", { className: "label", children: _jsx("span", { className: "label-text pt-3", children: "Overwrite Target Period" }) }), _jsxs("label", { className: "flex items-left py-2 px-3", children: [_jsx("input", { type: "checkbox", className: `sr-only peer items-left ${errors.overwrite_target ? "form-validation-fail" : ""}`, id: "overwrite_target", ...register("overwrite_target") }), _jsx("div", { className: "relative cursor-pointer w-11 h-6 py-3 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500" })] })] })] }), _jsx("div", { className: "form-control px-4 py-3", children: _jsxs("label", { className: "label cursor-pointer hidden", children: [_jsx("span", { className: "label-text", children: "Use cached response" }), _jsx("input", { type: "checkbox", className: "toggle", id: "useFakeApi" })] }) }), !hasPreviousSteps && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-full", id: "requestButton", "aria-label": "Update Model", onClick: handleSubmit(onSubmit), disabled: documentIsUploading, children: fetchingModelUpdate ? (_jsx("span", { className: "loading loading-spinner" })) : ("Update Model") }), !formSubmitted && (_jsx("button", { type: "button", className: "text-blue-500 px-4 py-2", id: "addAnotherStepButton", "aria-label": "Add Another Step", onClick: handleSubmit(onAddStep), disabled: documentIsUploading, children: "+ Add Another Step" }))] })), hasPreviousSteps && (_jsx("button", { type: "button", className: "bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded-full", id: "addUpdateStepButton", onClick: handleSubmit(onAddStep), disabled: documentIsUploading, children: "Add Update Step" })), _jsxs("div", { className: "pb-4", children: [_jsxs("div", { className: "max-w-full mx-2", children: [_jsx("p", { id: "response", className: "pt-2 break-words text-xs" }), isSubmitted && Object.keys(errors).length > 0 && (_jsx("p", { className: "pt-2 break-words text-xs text-red-500", children: Object.entries(errors).map(([fieldName, error]) => (_jsxs("span", { "data-testid": "form-error", children: [`${fieldName} ${error?.message}`, _jsx("br", {})] }, fieldName))) }))] }), _jsx("div", { className: "max-w-full mx-2", children: _jsx("p", { id: "userMessage", className: `pt-1 break-words text-xs pb-12 whitespace-pre-line ${userMessage ? "text-" + userMessage.level : ""}`, children: userMessage && userMessage.message }) })] })] })] }));
};
export default UpdaterForm;
